import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useTour } from '../context/TourContext';
import TourTooltip from './TourTooltip';

const OverlayContainer = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${theme.zIndex.modal};
  pointer-events: ${({ isVisible }) => isVisible ? 'auto' : 'none'};
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  transition: opacity ${theme.transitions.base};
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
`;

const Spotlight = styled.div<{ 
  top: number; 
  left: number; 
  width: number; 
  height: number; 
  borderRadius: number;
}>`
  position: absolute;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  box-shadow: 
    0 0 0 4px ${theme.colors.primary[400]},
    0 0 0 9999px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  transition: all ${theme.transitions.base};
  z-index: ${theme.zIndex.modal + 1};
  
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: ${({ borderRadius }) => borderRadius + 4}px;
    border: 2px solid ${theme.colors.primary[300]};
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 ${theme.colors.primary[400]}40;
    }
    70% {
      box-shadow: 0 0 0 10px ${theme.colors.primary[400]}00;
    }
    100% {
      box-shadow: 0 0 0 0 ${theme.colors.primary[400]}00;
    }
  }
`;

interface ElementBounds {
  top: number;
  left: number;
  width: number;
  height: number;
  borderRadius: number;
}

const TourOverlay: React.FC = () => {
  const { tourState, getCurrentStep } = useTour();
  const [elementBounds, setElementBounds] = useState<ElementBounds | null>(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  const calculateElementBounds = useCallback((element: HTMLElement): ElementBounds => {
    const rect = element.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(element);
    const borderRadius = parseInt(computedStyle.borderRadius) || 8;
    
    // Add some padding around the element
    const padding = 8;
    
    return {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + (padding * 2),
      height: rect.height + (padding * 2),
      borderRadius: borderRadius + padding,
    };
  }, []);

  const findTargetElement = useCallback((selector: string): HTMLElement | null => {
    // Try different methods to find the element
    let element = document.querySelector(selector) as HTMLElement | null;
    
    if (!element && selector.startsWith('#')) {
      // Try without the # for ID
      element = document.getElementById(selector.slice(1));
    }
    
    if (!element && selector.startsWith('.')) {
      // Try getElementsByClassName for class selectors
      const elements = document.getElementsByClassName(selector.slice(1));
      element = elements[0] as HTMLElement | null;
    }
    
    return element;
  }, []);

  const updateElementPosition = useCallback(() => {
    const currentStep = getCurrentStep();
    if (!currentStep || !tourState.isActive) {
      setElementBounds(null);
      setTargetElement(null);
      return;
    }

    const element = findTargetElement(currentStep.target);
    if (element) {
      setTargetElement(element);
      setElementBounds(calculateElementBounds(element));
      
      // Execute any action associated with this step
      if (currentStep.action) {
        currentStep.action();
      }
      
      // Scroll element into view if needed
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    } else {
      console.warn(`Tour: Target element not found: ${currentStep.target}`);
      setElementBounds(null);
      setTargetElement(null);
    }
  }, [getCurrentStep, tourState.isActive, findTargetElement, calculateElementBounds]);

  useEffect(() => {
    if (tourState.isActive) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(updateElementPosition, 100);
      return () => clearTimeout(timer);
    } else {
      setElementBounds(null);
      setTargetElement(null);
    }
  }, [tourState.isActive, tourState.currentStep, updateElementPosition]);

  useEffect(() => {
    if (!tourState.isActive) return;

    // Update position on resize or scroll
    const handleUpdate = () => {
      updateElementPosition();
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);
    
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [tourState.isActive, updateElementPosition]);

  if (!tourState.isActive) {
    return null;
  }

  const currentStep = getCurrentStep();
  if (!currentStep) {
    return null;
  }

  return (
    <OverlayContainer isVisible={tourState.isActive}>
      <Backdrop />
      
      {elementBounds && (
        <Spotlight
          top={elementBounds.top}
          left={elementBounds.left}
          width={elementBounds.width}
          height={elementBounds.height}
          borderRadius={elementBounds.borderRadius}
        />
      )}
      
      <TourTooltip
        step={currentStep}
        targetElement={targetElement}
        elementBounds={elementBounds}
      />
    </OverlayContainer>
  );
};

export default TourOverlay;